import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import css from './QuestionSection.module.css';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { fb, instagram, linkedIn, mail, map, phone } from './utils';
import {
  FieldTextInput,
  Form,
  Button,
  ExternalLink,
  NamedLink,
} from '../../../../components';
import { sendAnalyticsEvent } from '../../../../util/analytics';
import { StatusCode } from '../../../../util/enums';
import { showToaster } from '../../../../util/genericHelpers';
import { SendgridApi } from '../../../../util/api';

const QuestionSection = () => {
  const intl = useIntl();
  const [inProgress, setInProgress] = useState(false);
  const [done, setDone] = useState(false);

  const phoneNumber = intl.formatMessage({ id: 'CustomLandingPage.QuestionSection.phone' });
  const emailAddress = intl.formatMessage({ id: 'CustomLandingPage.QuestionSection.emailAddress' });

  const handleFormSubmit = async (values, form) => {
    setInProgress(true);
    setDone(false);
    try {
      sendAnalyticsEvent('button_click', 'submitted_contact_form');
      const { fName, lName, email, message, terms } = values;
      const emailResponse = await SendgridApi.submitContactForm({
        fName,
        lName,
        email,
        message,
        terms: !!terms,
      });
      if (emailResponse?.statusCode === StatusCode.SUCCESS) {
        setDone(true);
        showToaster('success', 'Message sent successfully');
        setTimeout(() => form.restart(), 0);
      } else {
        showToaster('error', 'Error while sending your message');
      }
    } catch (error) {
      showToaster('error', 'Error while sending your message');
    } finally {
      setInProgress(false);
    }
  };

  return (
    <div className={css.sectionWrapper}>
      <div className={css.wrapper}>
        <div className={css.sectionLeft}>
          <h2>
            Got a question?<br />
            Speak to a member<br />
            of the team
          </h2>

          <a className={css.contactLine} href={`tel:${phoneNumber.replace(/\s+/g, '')}`}>
            {phone} <span>{phoneNumber}</span>
          </a>
          <div className={css.contactLine}>
            {map}{' '}
            <span>{intl.formatMessage({ id: 'CustomLandingPage.QuestionSection.address' })}</span>
          </div>

          <a className={css.contactLine} href={`mailto:${emailAddress}`}>
            {mail} <span>{emailAddress}</span>
          </a>
          <div className={css.contactLineShare}>
            <ExternalLink className={css.socialShare} href="https://www.facebook.com/profile.php?id=61555715476988">
              {fb} Share
            </ExternalLink>
            <ExternalLink className={css.socialShare} href="https://www.instagram.com/the.modern.builder/">
              {instagram} Share
            </ExternalLink>
            <ExternalLink className={css.socialShare} href="https://www.linkedin.com/company/the-modern-builder/?viewAsMember=true">
              {linkedIn} Follow
            </ExternalLink>
          </div>
        </div>
        <div className={css.sectionRight}>
          <FinalForm
            mutators={{ ...arrayMutators }}
            initialValues={{}}
            onSubmit={handleFormSubmit}
            render={fieldRenderProps => {
              const { handleSubmit, values } = fieldRenderProps;
              const { fName, lName, email, message, terms } = values;

              const submitDisabled = !fName || !lName || !email || !message || !terms;

              return (
                <Form className={css.formWrapper} onSubmit={handleSubmit}>
                  <div className={css.name}>
                    <FieldTextInput
                      className={css.field}
                      type="text"
                      id="fName"
                      name="fName"
                      label={intl.formatMessage({
                        id: 'CustomLandingPage.QuestionSection.firstName',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'CustomLandingPage.QuestionSection.firstName',
                      })}
                    />

                    <FieldTextInput
                      className={css.field}
                      type="text"
                      id="lName"
                      name="lName"
                      label={intl.formatMessage({
                        id: 'CustomLandingPage.QuestionSection.lastName',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'CustomLandingPage.QuestionSection.lastName',
                      })}
                    />
                  </div>

                  <FieldTextInput
                    className={css.field}
                    type="text"
                    id="email"
                    name="email"
                    label={intl.formatMessage({ id: 'CustomLandingPage.QuestionSection.email' })}
                    placeholder="you@company.com"
                  />

                  <FieldTextInput
                    className={css.field}
                    type="textarea"
                    id="message"
                    name="message"
                    label={intl.formatMessage({ id: 'CustomLandingPage.QuestionSection.message' })}
                    placeholder="Leave us a message"
                  />

                  <Field name="terms" type="checkbox">
                    {({ input }) => (
                      <div className={css.termsRow}>
                        <label htmlFor="terms-check" className={css.checkboxLabel}>
                          <input id="terms-check" {...input} className={css.checkboxInput} />
                          <span className={input.checked ? `${css.checkboxBox} ${css.checkboxBoxChecked}` : css.checkboxBox}>
                            {input.checked && (
                              <svg width="11" height="9" viewBox="0 0 10 8" fill="none" style={{ display: 'block' }}>
                                <path d="M9 1L3.5 6.5L1 4" stroke="#FF6600" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </span>
                        </label>
                        <div className={css.termsText}>
                          {intl.formatMessage({ id: 'CustomLandingPage.QuestionSection.terms' })}
                          <NamedLink name="PrivacyPolicyPage" className={css.privacyPolicyLink}>
                            {intl.formatMessage({ id: 'CustomLandingPage.QuestionSection.privacyPolicy' })}
                          </NamedLink>
                        </div>
                      </div>
                    )}
                  </Field>
                  <Button
                    type="submit"
                    inProgress={inProgress}
                    disabled={submitDisabled}
                    ready={done}
                  >
                    Send message
                  </Button>
                </Form>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default QuestionSection;
